<script setup lang="ts">
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import AppHeader from '@/components/layout/AppHeader.vue'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import MobileGroupNav from '@/components/layout/MobileGroupNav.vue'
import { useGroupBadges } from '@/composables/useGroupBadges'
import { useUiStore } from '@/stores/ui'

/** Shell for the authenticated area: header on top, collapsible sidebar aside. */
const { t } = useI18n()
const ui = useUiStore()
const route = useRoute()

// Here and not in the sidebar: the sidebar is mounted twice (docked and
// drawer), and the badges need one poller each, alive on every page of the app.
useGroupBadges()

// Navigating away must close the mobile drawer, otherwise it covers the page.
watch(
  () => route.fullPath,
  () => ui.closeMobileSidebar(),
)
</script>

<template>
  <div class="flex min-h-dvh flex-col">
    <a href="#main-content" class="sr-only-focusable btn-gold absolute top-2 left-2 z-100">
      {{ t('common.skipToContent') }}
    </a>

    <AppHeader with-sidebar-toggle />

    <div class="flex flex-1">
      <!--
        Desktop: docked, collapsible, and pinned under the header — `self-start`
        plus an explicit height, otherwise the flex row stretches it to the full
        page and `sticky` would have nothing to slide against. That pinning is
        what makes the sidebar's own foot (help, contact, social) stay in view:
        the long menu scrolls inside the sidebar instead of scrolling the page.
      -->
      <AppSidebar class="sticky top-14 hidden h-[calc(100dvh-3.5rem)] self-start lg:flex" />

      <!--
        Mobile: off-canvas drawer, always expanded while open.
        No fade transition here either: an enter transition that stalls (which
        happens whenever requestAnimationFrame is starved, e.g. a hidden tab)
        would leave the drawer stuck at `opacity: 0` — present in the DOM but
        invisible, so the menu would simply appear not to open.
      -->
      <div v-if="ui.mobileSidebarOpen" class="fixed inset-0 z-50 flex lg:hidden">
        <div
          class="absolute inset-0 bg-black/60"
          role="presentation"
          @click="ui.closeMobileSidebar()"
        />
        <AppSidebar class="relative h-full" force-expanded />
      </div>

      <main id="main-content" class="min-w-0 flex-1 p-4 sm:p-6">
        <slot />
      </main>
    </div>

    <!-- Phone only: the selected group's four busiest pages, with the menu's numbers. -->
    <MobileGroupNav />
  </div>
</template>
